import {Constants} from "../constants";
import {IProduct, IProductGroup} from "./product-models";
import * as _ from 'lodash';
import {BaseComponent} from "../base.component";
import {BreadcrumbType} from "../breadcrumb/breadcrumb.models";
import {CartUtil} from "../cart/cart-util";
import {LoggerService} from "../common/logger.service";
import {UserService} from "../common/user.service";
import {BreadcrumbService} from "../breadcrumb/breadcrumb.service";
import {MarkupService} from "../common/markup.service";
import {AnalyticsService} from "../analytics/analytics.service";
import {JsonLdService} from "../seo/json-ld.service";
import {INavDisplay, ITag} from "../cart/catalog/catalog-models";
import {ProductReviewService} from "../productreview/product-review.service";
import {ChangeDetectorRef, Component, computed, OnDestroy, OnInit, signal} from "@angular/core";
import {CmsContentStackLocaleService, ContentStackLoadingStatus} from '@usana/ux/cms';
import {ProductService} from "./product.service";
import {IProductSummary} from "../productreview/product-review.models";
import {EMPTY, from, Subscription} from "rxjs";
import {switchMap, tap} from "rxjs/operators";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {GqlCartProductService} from "./graphql/gql-cart-product.service";
import {ProductUtil} from "../util/product-util";
import {IShopUser} from "../session/customer-models";
import {LocaleSwitcherService} from "../locale/locale-switcher.service";
import {LocaleUtil} from "../util/locale-util";
import {IProductResolverData} from "./product-models";
import {ProductRouteDataService} from "./product-route-data.service";

@Component({
    selector: 'product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
    standalone: false
})
export class ProductComponent extends BaseComponent implements OnInit, OnDestroy {

    productGroup: IProductGroup;
    product: IProduct;
    isLoading: boolean = false;
    recommendedProducts: IProduct[];
    bodyBenefitTags: INavDisplay[];
    productNumber: string;
    notFound: boolean = false;
    jsonLd: any = {};
    productReviewSubscription: Subscription | null = null;
    routeParamsSubscription: Subscription;
    isCelavive: boolean = false;
    showRecommendedProductsAiMessage: boolean = false;

    useCms = signal<boolean>(false);
    cmsStatus = signal<ContentStackLoadingStatus>(ContentStackLoadingStatus.LOADING);
    showLegacyPdp = computed(() => !this.useCms() || this.cmsStatus() === ContentStackLoadingStatus.NOT_FOUND);

    constructor(
        private readonly productService: ProductService,
        private readonly loggerService: LoggerService,
        private readonly userService: UserService,
        private readonly breadcrumbService: BreadcrumbService,
        private readonly analyticsService: AnalyticsService,
        private readonly productReviewService: ProductReviewService,
        private readonly jsonLdService: JsonLdService,
        private readonly markupService: MarkupService,
        private readonly gqlCartProductService: GqlCartProductService,
        private readonly route: ActivatedRoute,
        private readonly productRouteDataService: ProductRouteDataService,
        private localeSwitcherService: LocaleSwitcherService,
        private readonly cmsLocaleService: CmsContentStackLocaleService,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.isLoading = true;

        // Subscribe to route parameter changes to react when navigating between products
        this.routeParamsSubscription = this.route.paramMap.pipe(
            tap(() => {
                this.isLoading = true;
                // Force Angular to process the isLoading = true change immediately,
                // before the synchronous microtask path (guard cache + already-loaded user)
                // can flip isLoading back to false in the same Zone.js batch.
                // Without this, pdp-template is never destroyed between CMS product navigations.
                this.cdr.detectChanges();
            }),
            switchMap((params: ParamMap) => {
                const usanaIdParam = params.get('usanaId');
                const usanaId = usanaIdParam?.indexOf('-') > -1 ? ProductUtil.convertUsanaIdToDot(usanaIdParam) : usanaIdParam;
                const productData: IProductResolverData | undefined = this.productRouteDataService.getProductData(usanaId);

                if (!productData || productData.notFound) {
                    this.notFound = true;
                    this.markupService.setTitleFromTranslationTag("cart.product.missing.title");
                    this.isLoading = false;
                    return EMPTY;
                }

                this.onCmsStatus(ContentStackLoadingStatus.LOADING);
                this.productGroup = productData.productGroup;
                this.product = productData.product;
                this.notFound = false;
                this.loadUsanaId(usanaId);

                if (!this.productGroup || !this.product) {
                    this.notFound = true;
                    this.isLoading = false;
                    return EMPTY;
                }

                this.log.debug("getCartPDP", this.productGroup.id, this.productGroup);
                this.log.debug("getCartPDP, Product", this.product.id, this.product);

                if (this.showProductReviews) {
                    this.productReviewService.initProductReviews(this.product.id, this.product.usanaId);
                }

                return from(this.getCustomerData());
            }),
        ).subscribe({
            next: (user: IShopUser) => {
                this.applyProductFeaturesAfterCustomerLoaded(user);
            },
            error: (err: unknown) => {
                this.log.error("ProductComponent: getCustomerData failed", err);
                this.isLoading = false;
            },
        });
    }

    private applyProductFeaturesAfterCustomerLoaded(user: IShopUser): void {
        const locale = user?.usanaLocale ?? Constants.DEFAULT_LOCALE;
        // This sets the locale for the CMS requests and is looking for a "-" in the locale string.
        // Needs to be set before this.useCms is set to true.
        this.cmsLocaleService.setLocale(CmsContentStackLocaleService.transformIsoCode(LocaleUtil.getUrlLocale(locale)));
        this.useCms.set(user?.config?.useCms);
        this.showRecommendedProductsAiMessage = user?.config?.useBiRecommendedProducts;

        this.setBodyBenefits();
        this.getRecommendedProducts(this.product.id, this.product.usanaId);
        this.markupService.setOgSingleProductPage(this.product);
        this.setBreadcrumb();
        this.markupService.setTitle(this.product.productDescription.name + Constants.USANA_PAGE_TITLE_ENDING);
        this.log.debug("getCartPDP, Product", this.product.id, this.product);
        this.productService.prepareProductGroup(this.productGroup, this.product.id);
        this.analyticsService.viewProductEvent(this.product);
        this.isCelavive = CartUtil.isCelaviveCategory(this.product.productCategory.name);
        this.refreshJsonLd();
        this.isLoading = false;
    }

    ngOnDestroy() {
        this.productReviewSubscription?.unsubscribe();
        this.routeParamsSubscription?.unsubscribe();
    }

    onCmsStatus(status: ContentStackLoadingStatus) {
        // Show original pdp if not found
        this.cmsStatus.set(status);
        this.log.debug('CMS status:', status);
    }

    setBreadcrumb() {
        this.breadcrumbService.setBreadcrumb(this.product.id, BreadcrumbType.PRODUCT);
    }

    canAddToAutoOrder() {
        return CartUtil.showAddToAoButtonProduct(this.product);
    }

    loadUsanaId(usanaId: string) {
        //Store the usanaId with the "." instead of "-".
        const usanaProductId = usanaId;
        //check the stored value and get the part number
        if ((usanaProductId.match(new RegExp("\\.", 'g')) || []).length > 1) {
            //if id has more than 1 "." (oola and rise) we will just use the whole thing
            this.productNumber = usanaProductId;
        } else if (usanaProductId.indexOf('.') > -1) {
            this.productNumber = usanaProductId.split('.')[0];
        } else {
            this.productNumber = usanaProductId.substring(0, 3);
        }
    }

    private refreshJsonLd(): void {
        this.productReviewSubscription?.unsubscribe();
        this.productReviewSubscription = null;

        if (this.showProductReviews) {
            this.productReviewSubscription = this.productReviewService.productReviewObservable.subscribe((productReview: any) => {
                this.jsonLd = this.jsonLdService.getProductJson(this.product, productReview);
            });
        } else {
            this.jsonLd = this.jsonLdService.getProductJson(this.product, new IProductSummary());
        }
    }

    getRecommendedProducts(productId: number, usanaId: string) {
        if (this.limitedShopping) { // limited shopping experience does not need to fetch product recommends
            this.recommendedProducts = [];
        } else {
            this.gqlCartProductService.getRecommendedProducts(productId, usanaId).subscribe({
                next: (data: IProduct[]) => {
                    this.log.debug("getRecommendedProducts", data);
                    this.recommendedProducts = data ?? [];
                },
                error: (error) => {
                    this.log.error("getRecommendedProducts", error);
                    this.recommendedProducts = [];
                }
            })
        }
    }

    setBodyBenefits() {
        this.log.debug("getProductTags", this.product.productTags);
        this.bodyBenefitTags = _.filter(this.product.productTags, (tag: ITag) => {
            return !!tag.pdpDisplay && !!tag.parent && Constants.BODY_BENEFIT_TAG_NAME == tag.parent.name;
        }).map(tag => {
            return CartUtil.buildNavDisplay(tag);
        });
    }

    openLocaleSwitcher() {
        this.localeSwitcherService.openLocaleSwitcher();
    }
}
